import { useMemo } from "react";
import { Box, Card, Grid, CircularProgress, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import ByzDataTreeListCarePlan from "src/components/core-components/mui/data-grid/ByzDataTreeListCarePlan";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import TreeViewComponent from "src/components/core-components/mui/data-grid/tree-view";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";
import { formatDateTime } from "src/utils/date";
import { useTranslate } from "src/locales";
import { COMMON_GRID_HEADER_STYLE } from "./constants";
import { TreeData } from "./models";

interface PatientCareGridsProps {
  treeRows: TreeData[];
  isDataLoading: boolean;
  selectedChildDetails: any[];
  selectedPlanPeriods: any[];
  selectedIds: number[];
  date: string;
  copiedGuideData: any;
  onSelectionChange: (periods: any[]) => void;
  onChildSelectionChange: (details: any[]) => void;
  onDeleteGuide: (id: number) => void;
  onDeletePlan: (id: number) => void;
  onCopyGuide: (id: number) => void;
  onPasteGuide: () => void;
  onEditGuidePlan: (row: TreeData) => void;
  onEditIntervention: (row: any) => void;
  onEditPlanning: (row: any) => void;
  onTreeSelect: (id: number) => void;
  onHandleProcessAction: (id: number) => void;
  onDeleteIntervention: (id: number) => void;
  onDeletePlanning: (id: number) => void;
  setSelectedRowForPeriodPlanning: (id: number) => void;
  setSelectedRowDataForNewPeriod: (row: any) => void;
  lastSelectedTreeId: number | null;
  setLastSelectedTreeId: (id: number | null) => void;
  lastSelectedInterventionId: number | null;
  setLastSelectedInterventionId: (id: number | null) => void;
  setSelectedIds: (ids: number[]) => void;
}

const PatientCareGrids = ({
  treeRows, isDataLoading, selectedChildDetails, selectedPlanPeriods, selectedIds,
  date, copiedGuideData, onSelectionChange, onChildSelectionChange,
  onDeleteGuide, onDeletePlan, onCopyGuide, onPasteGuide,
  onEditGuidePlan, onEditIntervention, onEditPlanning,
  onTreeSelect, onHandleProcessAction, onDeleteIntervention, onDeletePlanning,
  setSelectedRowForPeriodPlanning, setSelectedRowDataForNewPeriod,
  lastSelectedTreeId, setLastSelectedTreeId,
  lastSelectedInterventionId, setLastSelectedInterventionId,
  setSelectedIds
}: PatientCareGridsProps) => {
  const { t } = useTranslate();

  const treeViewData = [
    {
      id: 1,
      name: t("patient-care.columns.selected-intervention-plan"),
      children: [
        { id: 2, name: t("patient-care.columns.according-to-defined-period") },
        { id: 3, name: t("patient-care.columns.new-period-plan") },
      ],
    },
  ];

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "no",
      headerName: t("patient-care.columns.no"),
      width: 20,
      headerClassName: "header-theme",
    },
    {
      field: "name",
      headerName: t("patient-care.columns.plan"),
      flex: 1,
      headerClassName: "header-theme",
      renderCell: (params: any) => {
        if (params.row.parentId === null) {
          return (
            <span style={{ fontWeight: 'bold' }}>
              {t("patient-care.guide-name")} : {params.value}
            </span>
          );
        }
        return params.value;
      }
    },
    {
      field: "target",
      headerName: t("patient-care.columns.target"),
      flex: 1,
      headerClassName: "header-theme",
    },
    {
      field: "is_reached",
      headerName: t("patient-care.columns.reached"),
      width: 100,
      headerClassName: "header-theme",
      renderCell: (params) => (
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}
        >
          {params.row.parentId !== null && (
            <ByzMuiCheckBox circular={true} checked={params.row.is_reached === "1"} sx={{ pointerEvents: 'none' }} />
          )}
        </Box>
      ),
    },
    {
      field: "is_not_reached",
      headerName: t("patient-care.columns.notreached"),
      width: 100,
      headerClassName: "header-theme",
      renderCell: (params) => (
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}
        >
          {params.row.parentId !== null && (
            <ByzMuiCheckBox circular={true} checked={params.row.is_not_reached === "1"} sx={{ pointerEvents: 'none' }} />
          )}
        </Box>
      ),
    },
    {
      field: "plan_time",
      headerName: t("patient-care.columns.processtime"),
      width: 150,
      headerClassName: "header-theme",
      renderCell: (params) => (
        formatDateTime(params.value)
      ),
    },
    {
      field: "cuser",
      headerName: t("patient-care.columns.processor"),
      width: 150,
      headerClassName: "header-theme",
    },
    {
      field: "note",
      headerName: t("patient-care.columns.note"),
      width: 150,
      headerClassName: "header-theme",
    },
    {
      field: "edit",
      headerName: t("patient-care.columns.update"),
      width: 100,
      headerClassName: "header-theme",
      renderCell: (params: GridRenderCellParams) => (
        <Grid>
          {params.row.parentId !== null && (
            <EditIcon
              color="info"
              onClick={() => onEditGuidePlan(params.row)}
              style={{ cursor: "pointer" }}
            />
          )}
        </Grid>
      )
    },
  ], [t, onEditGuidePlan]);

  const columnsNurse: GridColDef[] = useMemo(() => [
    {
      field: "no",
      headerName: t("patient-care.columns.no"),
      width: 100,
      headerClassName: "header-theme",
    },
    {
      field: "ncpd_name",
      headerName: t("patient-care-guide.nursing-initiatives"),
      flex: 1,
      headerClassName: "header-theme",
    },
    {
      field: "period",
      headerName: t("patient-care-guide.period"),
      flex: 1,
      headerClassName: "header-theme",
      renderCell: (params: any) => (
        <span>
          {params.value && !isNaN(parseFloat(params.value)) ? `${parseFloat(params.value) / 60} ${t("hour")}` : ""}
        </span>
      ),
    },
    {
      field: "is_reached",
      headerName: t("patient-care-guide.reached"),
      width: 100,
      headerClassName: "header-theme",
      renderCell: (params) => (
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}
        >
          {params.row.parentId !== null && (
            <ByzMuiCheckBox circular={true} checked={params.row.is_reached === "1"} sx={{ pointerEvents: 'none' }} />
          )}
        </Box>
      ),
    },
    {
      field: "is_not_reached",
      headerName: t("patient-care-guide.notreached"),
      width: 100,
      headerClassName: "header-theme",
      renderCell: (params) => (
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}
        >
          {params.row.parentId !== null && (
            <ByzMuiCheckBox circular={true} checked={params.row.is_not_reached === "1"} sx={{ pointerEvents: 'none' }} />
          )}
        </Box>
      ),
    },
    {
      field: "is_continues",
      headerName: t("patient-care-guide.continues"),
      width: 100,
      headerClassName: "header-theme",
      renderCell: (params) => (
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}
        >
          {params.row.parentId !== null && (
            <ByzMuiCheckBox circular={true} checked={params.row.is_continues === "1"} sx={{ pointerEvents: 'none' }} />
          )}
        </Box>
      ),
    },
    {
      field: "process_time",
      headerName: t("patient-care-guide.processtime"),
      width: 150,
      headerClassName: "header-theme",
      renderCell: (params) => (
        formatDateTime(params.value)
      ),
    },
    {
      field: "cuser",
      headerName: t("patient-care-guide.processor"),
      width: 150,
      headerClassName: "header-theme",
    },
    {
      field: "note",
      headerName: t("patient-care-guide.note"),
      width: 150,
      headerClassName: "header-theme",
    },
    {
      field: "edit",
      headerName: t("patient-care-guide.columns.edit"),
      width: 100,
      headerClassName: "header-theme",
      renderCell: (params: GridRenderCellParams) => (
        <Grid>
          {params.row.id !== null && (
            <EditIcon
              color="info"
              onClick={() => onEditIntervention(params.row)}
              style={{ cursor: "pointer" }}
            />
          )}
        </Grid>
      )
    },
  ], [t, onEditIntervention]);

  const columnsPlan: GridColDef[] = useMemo(() => [
    {
      field: "no",
      headerName: t("patient-care.columns.no"),
      width: 100,
      headerClassName: "header-theme",
      renderCell: (params) => (
        <Box sx={{ paddingLeft: 2 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "time",
      headerName: t("patient-care.columns.seans"),
      width: 250,
      headerClassName: "header-theme",
      renderCell: (params) => (
        formatDateTime(params.value)
      ),
    },
    {
      field: "is_implemented",
      headerName: t("patient-care.columns.applied"),
      width: 100,
      headerClassName: "header-theme",
      renderCell: (params: any) => (
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}
        >
          <ByzMuiCheckBox
            circular={true}
            checked={params.row.is_implemented === "1" || params.row.is_implemented === 1}
            sx={{ pointerEvents: 'none' }}
          />
        </Box>
      ),
    },
    {
      field: "is_not_implemented",
      headerName: t("patient-care.columns.not-applied"),
      width: 100,
      headerClassName: "header-theme",
      renderCell: (params: any) => (
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}
        >
          <ByzMuiCheckBox
            circular={true}
            checked={params.row.is_not_implemented === "1" || params.row.is_not_implemented === 1}
            sx={{ pointerEvents: 'none' }}
          />
        </Box>
      ),
    },
    {
      field: "process_time",
      headerName: t("patient-care.columns.processtime"),
      width: 150,
      headerClassName: "header-theme",
      renderCell: (params) => (
        formatDateTime(params.value)
      ),
    },
    {
      field: "cuser",
      headerName: t("patient-care.columns.processor"),
      width: 150,
      headerClassName: "header-theme",
    },
    {
      field: "note",
      headerName: t("patient-care.columns.note"),
      width: 150,
      headerClassName: "header-theme",
    },
    {
      field: "edit",
      headerName: t("patient-care-guide.columns.edit"),
      width: 100,
      headerClassName: "header-theme",
      renderCell: (params: any) => (
        <Grid>
          {params.row.id !== null && (
            <EditIcon
              color="info"
              onClick={() => onEditPlanning(params.row)}
              style={{ cursor: "pointer" }}
            />
          )}
        </Grid>
      ),
    },
  ], [t, onEditPlanning]);

  const actionColumn: GridColDef = {
    field: "actions",
    headerName: "",
    width: 90,
    renderHeader: () => (
      <ByzMuiOpenProcessMenu
        menuItems={[
          {
            label: (
              <TreeViewComponent
                data={treeViewData}
                onSelect={onTreeSelect}
              />
            ),
            onClick: () => { },
          },
          {
            label: t("patient-care.delete-selected-intervention"),
            onClick: () => {
              onDeleteIntervention(0);
            },
          },
          {
            label: t("patient-care.delete-planning"),
            onClick: () => {
              onDeletePlanning(0);
            },
          },
        ]}
        inSearch={true}
        onOpen={() => {
          onHandleProcessAction(0);
          setSelectedRowForPeriodPlanning(0);
          setSelectedRowDataForNewPeriod(null);
        }}
      />
    ),
    renderCell: (params: GridRenderCellParams) => (
      <ByzMuiOpenProcessMenu
        menuItems={[
          {
            label: (
              <TreeViewComponent
                data={treeViewData}
                onSelect={onTreeSelect}
              />
            ),
            onClick: () => { },
          },
          {
            label: t("patient-care.delete-selected-intervention"),
            onClick: () => {
              onDeleteIntervention(params.row.id);
            },
          },
          {
            label: t("patient-care.delete-planning"),
            onClick: () => {
              onDeletePlanning(params.row.id);
            },
          },
        ]}
        inSearch={true}
        onOpen={() => {
          onHandleProcessAction(params.row.id);
          setSelectedRowForPeriodPlanning(params.row.id);
          setSelectedRowDataForNewPeriod(params.row);
        }}
      />
    ),
  };

  const numberedChildDetails = useMemo(() => selectedChildDetails.map((item, idx) => ({ ...item, no: idx + 1 })), [selectedChildDetails]);
  const numberedPlanPeriods = useMemo(() => selectedPlanPeriods.map((item, idx) => ({ ...item, no: idx + 1 })), [selectedPlanPeriods]);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Card sx={{ marginBottom: "5px" }}>
          <Grid
            container
            sx={{ margin: "8px", p: 2, borderBottom: "1px solid #E0E0E0" }}
          >
            <span style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
              {t("patient-care.planning-added-patient")}
            </span>
          </Grid>
          {isDataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{t("loading")}</Typography>
            </Box>
          ) : (
            <ByzDataTreeListCarePlan
              initialData={treeRows}
              columns={columns}
              autoHeight={true}
              onSelectionChange={(ids) => {
                const newId = ids[0];
                if (lastSelectedTreeId === newId) return;
                setLastSelectedTreeId(newId);

                const selected = treeRows.find(row => row.id === newId);
                if (selected && selected.parentId !== null) {
                  const details = selected.patientnursecareplandetails || [];
                  const detailsWithPlanName = details.map((detail: any) => ({ ...detail, planName: selected.name }));
                  onChildSelectionChange(detailsWithPlanName);
                } else {
                  onChildSelectionChange([]);
                }
              }}
              onDeleteGuide={onDeleteGuide}
              onDeletePlan={onDeletePlan}
              onCopyGuide={onCopyGuide}
              onPasteGuide={onPasteGuide}
              canPasteGuide={copiedGuideData !== null && (() => {
                const currentDate = new Date();
                currentDate.setHours(0, 0, 0, 0);
                const selectedDate = new Date(date + "T00:00:00");
                return selectedDate >= currentDate;
              })()}
              sx={{
                marginLeft: "16px",
                marginRight: "16px",
                ...COMMON_GRID_HEADER_STYLE
              }}
              treeColumn="no"
              whiteGrid={true}
              customToolbar={ByzDataGridToolbar}
            />
          )}
        </Card>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Card sx={{ marginBottom: "5px" }}>
          <Grid
            container
            sx={{ margin: "8px", p: 2, borderBottom: "1px solid #E0E0E0" }}
          >
            <span style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
              {t("patient-care.nursing-intervention")}
            </span>
          </Grid>
          {isDataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{t("loading")}</Typography>
            </Box>
          ) : (
            <ByzMuiDataGrid
              customToolbar={ByzDataGridToolbar}
              columns={[actionColumn, ...columnsNurse]}
              disableProcessMenu={true}
              pageSize={10}
              rows={numberedChildDetails}
              autoHeight={numberedChildDetails.length === 0}
              checkboxSelection={false}
              rowSelectionModel={selectedIds.length > 0 ? [selectedIds[0]] : []}
              onRowSelectionModelChange={(newSelection) => {
                const id = Array.isArray(newSelection) ? newSelection[0] : newSelection;
                if (lastSelectedInterventionId === id) return;
                setLastSelectedInterventionId(id as number);

                setSelectedIds(id ? [id as number] : []);
                const selected = selectedChildDetails.find(row => row.id === id);
                if (selected && Array.isArray(selected.patientnursecareplandetailsperiod)) {
                  onSelectionChange(selected.patientnursecareplandetailsperiod);
                } else {
                  onSelectionChange([]);
                }
              }}
              sx={{
                marginLeft: "8px",
                marginRight: "8px",
                ...COMMON_GRID_HEADER_STYLE
              }}
              whiteGrid={true}
            />
          )}
        </Card>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Card sx={{ marginBottom: "5px" }}>
          <Grid
            container
            sx={{ margin: "8px", p: 2, borderBottom: "1px solid #E0E0E0" }}
          >
            <span style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
              {t("patient-care.planing")}
            </span>
          </Grid>
          {isDataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{t("loading")}</Typography>
            </Box>
          ) : (
            <ByzMuiDataGrid
              customToolbar={ByzDataGridToolbar}
              columns={columnsPlan}
              disableProcessMenu={true}
              pageSize={10}
              rows={numberedPlanPeriods}
              autoHeight={numberedPlanPeriods.length === 0}
              sx={{
                marginLeft: "8px",
                marginRight: "8px",
                ...COMMON_GRID_HEADER_STYLE
              }}
              whiteGrid={true}
            />
          )}
        </Card>
      </Box>
    </>
  );
};

export default PatientCareGrids;
